import { useEffect, useMemo, useRef } from 'react';

/**
 * Hook to detect clicks outside of a referenced element and ESC key press
 * @param {Function} handler - Callback function to execute when clicking outside or pressing ESC
 * @returns {React.RefObject} - Ref to attach to the element
 */
const useClickOutside = (handler, disableClickOutside) => {
  const ref = useRef(null);
  const handlerRef = useRef(handler);
  const disableRef = useMemo(() => disableClickOutside, [disableClickOutside]);
  // Update handler ref when handler changes without re-running effect
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    if (disableRef) return;
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        handlerRef.current();
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        handlerRef.current();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [disableRef]); // Empty dependency array - only runs once on mount

  return ref;
};

export default useClickOutside;
